import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { validateDaySchedule, type ShiftData, type OpeningHoursData, type DayValidationResult, type CustomClosedDateOptions } from './schedule-validation';
import { BusinessClosedDate } from '@/types';

/**
 * Unified Shift Validation Service
 *
 * This service provides a single source of truth for shift validation status calculations.
 * It ensures consistent data fetching, transformation, and validation across all components.
 *
 * Key principles:
 * - Single data fetching pipeline from Supabase
 * - Standardized ShiftData transformation
 * - Consistent opening hours integration
 * - Unified caching strategy
 */

export interface UnifiedShiftValidationOptions {
  businessId: string;
  includeOpeningHours?: boolean;
  includeEmployeeData?: boolean;
  cacheKey?: string;
}

export interface ShiftDataWithEmployee extends ShiftData {
  status?: string; // Pre-calculated status from shifts_history
  employee?: {
    id: string;
    name: string;
  } | null;
}

export interface DayShiftValidationResult extends DayValidationResult {
  date: Date;
  dateKey: string;
  shifts?: ShiftDataWithEmployee[];
  employees?: Array<{ id: string; name: string }>;
  employeeCount?: number;
}

/**
 * Cache for validation results to avoid repeated calculations
 * Key format: `${businessId}:${dateKey}:${includeOpeningHours}:${includeEmployeeData}`
 */
const validationCache = new Map<string, {
  result: DayShiftValidationResult;
  timestamp: number;
  ttl: number;
}>();

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache TTL
const STALE_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes stale cache (still usable while revalidating)

/**
 * Clear expired cache entries
 */
const clearExpiredCache = () => {
  const now = Date.now();
  for (const [key, entry] of validationCache.entries()) {
    if (now > entry.timestamp + entry.ttl) {
      validationCache.delete(key);
    }
  }
};

/**
 * Fetch shifts for a specific date from Supabase
 * Returns raw database format to ensure consistency
 */
const fetchShiftsForDate = async (
  businessId: string,
  date: Date,
  includeEmployeeData = false
): Promise<ShiftDataWithEmployee[]> => {
  // Create date range for the entire day
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);

  // Build select query with optional employee join
  // ALWAYS fetch status field from shifts_history for attendance tracking
  const selectQuery = includeEmployeeData
    ? 'id, start_time, end_time, employee_id, status, employees:employee_id(id, name)'
    : 'id, start_time, end_time, employee_id, status';

  const { data: shiftsData, error } = await supabase
    .from('shifts_history')
    .select(selectQuery)
    .eq('business_id', businessId)
    .gte('start_time', startOfDay.toISOString())
    .lt('start_time', endOfDay.toISOString());

  if (error) {
    console.error('Error fetching shifts for validation:', error);
    return [];
  }

  // Return in standardized ShiftData format with pre-calculated status
  return (shiftsData || []).map(shift => ({
    id: shift.id,
    employee_id: shift.employee_id, // Keep snake_case from database
    start_time: shift.start_time, // Keep ISO string from database
    end_time: shift.end_time, // Keep ISO string from database
    status: shift.status, // Pre-calculated status from shifts_history
    employee: includeEmployeeData && shift.employees ? {
      id: shift.employees.id,
      name: shift.employees.name
    } : null
  }));
};

/**
 * Fetch opening hours for a business
 * Returns standardized OpeningHoursData format
 */
const fetchOpeningHours = async (businessId: string): Promise<OpeningHoursData[]> => {
  const { data: openingHoursData, error } = await supabase
    .from('business_opening_hours')
    .select('id, day_of_week, opening_time, closing_time, is_closed_all_day, is_24_hours')
    .eq('business_id', businessId);

  if (error) {
    console.error('Error fetching opening hours for validation:', error);
    return [];
  }

  return (openingHoursData || []).map(oh => ({
    id: oh.id,
    day_of_week: oh.day_of_week?.toString() || '0',
    opening_time: oh.opening_time || '09:00',
    closing_time: oh.closing_time || '17:00',
    is_closed_all_day: oh.is_closed_all_day || false,
    is_24_hours: oh.is_24_hours || false
  }));
};

/**
 * Get opening hours for a specific day of the week
 * dayOfWeek: 0=Sunday, 1=Monday, ..., 6=Saturday (from date-fns getDay())
 */
const getOpeningHoursForDay = (openingHours: OpeningHoursData[], dayOfWeek: number): OpeningHoursData | null => {
  // Map JavaScript's getDay() numbers to day names as stored in database
  const dayNameMap: { [key: number]: string } = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday'
  };

  const dayName = dayNameMap[dayOfWeek];
  if (!dayName) return null;

  return openingHours.find(oh => oh.day_of_week === dayName) || null;
};

/**
 * Fetch custom closed date for a specific date
 * Returns null if the date is not marked as closed
 */
const fetchCustomClosedDate = async (
  businessId: string,
  date: Date
): Promise<BusinessClosedDate | null> => {
  const dateKey = format(date, 'yyyy-MM-dd');

  const { data, error } = await supabase
    .from('business_closed_dates')
    .select('id, business_id, closed_date, reason, created_by, created_at, updated_at')
    .eq('business_id', businessId)
    .eq('closed_date', dateKey)
    .maybeSingle();

  if (error) {
    console.error('Error fetching custom closed date:', error);
    return null;
  }

  return data as BusinessClosedDate | null;
};

/**
 * Validate shift status for a single day
 * This is the main function that components should use for consistent validation
 */
export const validateDayShifts = async (
  date: Date,
  options: UnifiedShiftValidationOptions
): Promise<DayShiftValidationResult> => {
  const { businessId, includeOpeningHours = true, includeEmployeeData = false } = options;
  const dateKey = format(date, 'yyyy-MM-dd');
  const cacheKey = `${businessId}:${dateKey}:${includeOpeningHours}:${includeEmployeeData}`;

  // Clear expired cache entries periodically
  clearExpiredCache();

  // Check cache first with stale-while-revalidate pattern
  const cachedResult = validationCache.get(cacheKey);
  const now = Date.now();

  // Return fresh cache immediately
  if (cachedResult && now <= cachedResult.timestamp + cachedResult.ttl) {
    return cachedResult.result;
  }

  // Return stale cache but trigger background revalidation
  if (cachedResult && now <= cachedResult.timestamp + STALE_CACHE_TTL_MS) {
    // Return stale data immediately for better UX
    const staleResult = cachedResult.result;

    // Trigger background revalidation (don't await)
    (async () => {
      try {
        const [shifts, openingHours, customClosedDate] = await Promise.all([
          fetchShiftsForDate(businessId, date, includeEmployeeData),
          includeOpeningHours ? fetchOpeningHours(businessId) : Promise.resolve([]),
          fetchCustomClosedDate(businessId, date)
        ]);

        const dayOfWeek = date.getDay();
        const dayOpeningHours = includeOpeningHours
          ? getOpeningHoursForDay(openingHours, dayOfWeek)
          : null;

        // Build custom closed options
        const customClosedOptions: CustomClosedDateOptions | undefined = customClosedDate ? {
          isCustomClosed: true,
          closureReason: customClosedDate.reason
        } : undefined;

        const validation = validateDaySchedule(date, shifts, dayOpeningHours, customClosedOptions);

        // Extract employee data if requested
        const employees = includeEmployeeData
          ? shifts
              .filter(s => s.employee_id && s.employee)
              .map(s => s.employee!)
              .filter((e, i, arr) => arr.findIndex(emp => emp.id === e.id) === i) // Unique employees
          : undefined;

        const result: DayShiftValidationResult = {
          ...validation,
          date: new Date(date),
          dateKey,
          shifts: includeEmployeeData ? shifts : undefined,
          employees,
          employeeCount: employees?.length
        };

        // Update cache with fresh data
        validationCache.set(cacheKey, {
          result,
          timestamp: Date.now(),
          ttl: CACHE_TTL_MS
        });
      } catch (error) {
        console.error('Background revalidation failed:', error);
      }
    })();

    return staleResult;
  }

  try {
    // Fetch data in parallel for better performance (including custom closed dates)
    const [shifts, openingHours, customClosedDate] = await Promise.all([
      fetchShiftsForDate(businessId, date, includeEmployeeData),
      includeOpeningHours ? fetchOpeningHours(businessId) : Promise.resolve([]),
      fetchCustomClosedDate(businessId, date)
    ]);

    // Get opening hours for this specific day
    const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    const dayOpeningHours = includeOpeningHours
      ? getOpeningHoursForDay(openingHours, dayOfWeek)
      : null;

    // Build custom closed options
    const customClosedOptions: CustomClosedDateOptions | undefined = customClosedDate ? {
      isCustomClosed: true,
      closureReason: customClosedDate.reason
    } : undefined;

    // Validate using the standard validation function
    const validation = validateDaySchedule(date, shifts, dayOpeningHours, customClosedOptions);

    // Extract employee data if requested
    const employees = includeEmployeeData
      ? shifts
          .filter(s => s.employee_id && s.employee)
          .map(s => s.employee!)
          .filter((e, i, arr) => arr.findIndex(emp => emp.id === e.id) === i) // Unique employees
      : undefined;

    const result: DayShiftValidationResult = {
      ...validation,
      date: new Date(date), // Create new Date object to avoid reference issues
      dateKey,
      shifts: includeEmployeeData ? shifts : undefined,
      employees,
      employeeCount: employees?.length
    };

    // Cache the result
    validationCache.set(cacheKey, {
      result,
      timestamp: Date.now(),
      ttl: CACHE_TTL_MS
    });

    return result;
  } catch (error) {
    console.error('Error in validateDayShifts:', error);

    // Return fallback result on error
    return {
      status: 'red',
      hasShifts: false,
      allShiftsAssigned: false,
      shiftsCount: 0,
      validationMessage: 'Error loading shift data',
      openingHoursCovered: false,
      coverageGaps: [],
      date: new Date(date),
      dateKey
    };
  }
};

/**
 * Validate multiple days at once for better performance
 * Useful for calendar views that need to validate entire months
 */
export const validateMultipleDays = async (
  dates: Date[],
  options: UnifiedShiftValidationOptions
): Promise<Map<string, DayShiftValidationResult>> => {
  const results = new Map<string, DayShiftValidationResult>();

  // Process dates in parallel with limited concurrency to avoid overwhelming the database
  const BATCH_SIZE = 5;
  const batches = [];

  for (let i = 0; i < dates.length; i += BATCH_SIZE) {
    batches.push(dates.slice(i, i + BATCH_SIZE));
  }

  for (const batch of batches) {
    const batchPromises = batch.map(date => validateDayShifts(date, options));
    const batchResults = await Promise.all(batchPromises);

    batchResults.forEach(result => {
      results.set(result.dateKey, result);
    });
  }

  return results;
};

/**
 * Clear cache for a specific business or all cache
 * Useful when shifts are modified and validation needs to be recalculated
 */
export const clearValidationCache = (businessId?: string) => {
  if (businessId) {
    // Clear cache entries for specific business
    for (const [key] of validationCache.entries()) {
      if (key.startsWith(`${businessId}:`)) {
        validationCache.delete(key);
      }
    }
  } else {
    // Clear all cache
    validationCache.clear();
  }
};

/**
 * Transform external shift data to ShiftData format
 * Useful when components have shift data in different formats and need to validate manually
 */
export const transformToShiftData = (shifts: any[]): ShiftData[] => {
  return shifts.map(shift => ({
    id: shift.id,
    employee_id: shift.employee_id || shift.employeeId || null, // Handle both formats
    start_time: shift.start_time || (shift.startTime instanceof Date ? shift.startTime.toISOString() : shift.startTime),
    end_time: shift.end_time || (shift.endTime instanceof Date ? shift.endTime.toISOString() : shift.endTime)
  }));
};

/**
 * Get validation status color class for UI components
 * Standardized across the entire application
 */
export const getValidationStatusClass = (status: DayValidationResult['status'], type: 'background' | 'border' | 'text' = 'border'): string => {
  const colorMap = {
    green: {
      background: 'bg-green-100 dark:bg-green-900/30',
      border: 'border-l-2 border-l-green-500 dark:border-l-green-400',
      text: 'text-green-800 dark:text-green-300'
    },
    orange: {
      background: 'bg-orange-100 dark:bg-orange-900/30',
      border: 'border-l-2 border-l-orange-500 dark:border-l-orange-400',
      text: 'text-orange-800 dark:text-orange-300'
    },
    red: {
      background: 'bg-red-100 dark:bg-red-900/30',
      border: 'border-l-2 border-l-red-500 dark:border-l-red-400',
      text: 'text-red-800 dark:text-red-300'
    },
    closed: {
      background: 'bg-gray-100 dark:bg-gray-800/30',
      border: 'border-l-0', // Zero width left border for closed days
      text: 'text-gray-700 dark:text-gray-300'
    }
  };

  return colorMap[status]?.[type] || colorMap.red[type];
};

export type { ShiftData, OpeningHoursData, DayValidationResult, ShiftDataWithEmployee };